import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { SITE, NAV_LINKS, SERVICES } from "@/lib/siteData";

export default function SiteFooter() {
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-[#16302B] text-[#F9F8F6]">
      <div className="mx-auto max-w-7xl px-6 pt-20 pb-10 md:px-12 md:pt-28">
        <div className="grid gap-12 md:grid-cols-12">
          {/* Brand & contact */}
          <div className="md:col-span-5">
            <p className="eyebrow text-[#D95D39]">Mulberry Land Services</p>
            <h2 className="mt-3 font-display text-3xl font-black leading-[0.95] tracking-tight md:text-4xl">
              Ready when your land is.
            </h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-[#F9F8F6]/70">
              Family-run forestry mulching and earthworks crew. Insured,
              experienced, and respectful of every acre we touch.
            </p>

            <ul className="mt-8 space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Phone size={16} strokeWidth={2.5} className="mt-0.5 text-[#D95D39]" />
                <a href={SITE.phoneHref} className="hover:text-[#D95D39]" data-testid="footer-phone">
                  {SITE.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} strokeWidth={2.5} className="mt-0.5 text-[#D95D39]" />
                <a href={`mailto:${SITE.email}`} className="hover:text-[#D95D39]" data-testid="footer-email">
                  {SITE.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} strokeWidth={2.5} className="mt-0.5 text-[#D95D39]" />
                <span>{SITE.address}</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={16} strokeWidth={2.5} className="mt-0.5 text-[#D95D39]" />
                <span>{SITE.hours}</span>
              </li>
            </ul>
          </div>

          {/* Nav */}
          <div className="md:col-span-3">
            <p className="eyebrow text-[#F9F8F6]/60">Navigate</p>
            <ul className="mt-5 space-y-3 text-sm font-semibold">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-[#F9F8F6]/85 transition-colors hover:text-[#D95D39]"
                    data-testid={`footer-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="md:col-span-4">
            <p className="eyebrow text-[#F9F8F6]/60">Services</p>
            <ul className="mt-5 grid grid-cols-1 gap-3 text-sm font-semibold sm:grid-cols-2">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to="/services"
                    className="text-[#F9F8F6]/85 transition-colors hover:text-[#D95D39]"
                    data-testid={`footer-service-${s.slug}`}
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Wordmark */}
        <div
          aria-hidden
          className="pointer-events-none mt-16 select-none overflow-hidden font-display text-[18vw] font-black leading-none tracking-tighter text-[#F9F8F6]/[0.06] md:mt-24 md:text-[14vw]"
        >
          MULBERRY
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-4 border-t border-[#F9F8F6]/10 pt-8 text-xs text-[#F9F8F6]/60 md:flex-row md:items-center">
          <p>
            © {new Date().getFullYear()} {SITE.name}. Licensed & Insured.
          </p>
          <p>
            Site crafted with care · Est. {SITE.founded}
          </p>
        </div>
      </div>
    </footer>
  );
}
